import { useParams, Navigate, Link } from "react-router-dom";
import { useSEO } from "../components/SEO";
import { Reveal, SectionTag } from "../components/Reveal";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { JsonLd } from "../components/JsonLd";
import { articleSchema, faqSchema } from "../lib/schema";
import { ArrowLink } from "../components/CTA";
import { CtaBand } from "../components/CtaBand";
import { getArticleBySlug, ARTICLES } from "../data/articles";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function Article() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useSEO(
    article ? `${article.title} | Nexus Catalyst` : "Resource Hub | Nexus Catalyst",
    article ? article.description : "Hajj and Umrah marketing resources for Indian travel agencies.",
    { ogType: article ? "article" : "website" }
  );

  if (!article) return <Navigate to="/resources" replace />;

  const related = article.related
    .map((s) => ARTICLES.find((a) => a.slug === s))
    .filter(Boolean);

  return (
    <main data-testid={`article-${article.slug}`}>
      <JsonLd
        data={articleSchema({
          title: article.title,
          description: article.description,
          path: `/resources/${article.slug}`,
          datePublished: article.datePublished,
        })}
      />
      <JsonLd data={faqSchema(article.faqs)} />

      <section className="relative bg-cream pt-32 pb-16 md:pt-44 md:pb-20 overflow-hidden" data-testid="article-hero">
        <div className="absolute inset-0 geo-pattern opacity-[0.05]" />
        <div className="relative max-w-4xl mx-auto px-6">
          <Reveal>
            <Breadcrumbs
              items={[
                { label: "Resources", to: "/resources" },
                { label: article.cluster, to: `/resources/${article.slug}` },
              ]}
            />
            <span className="inline-block border border-gold/40 bg-white px-3 py-1 text-[10px] font-semibold tracking-[0.2em] uppercase text-tan mb-6">
              {article.cluster}
            </span>
          </Reveal>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-charcoal leading-[1.08]">
            {article.title}
          </h1>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <div className="border-l-2 border-gold bg-cream p-6 md:p-8" data-testid="article-direct-answer">
              <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-3">
                In Short
              </div>
              <p className="text-base md:text-lg text-charcoal/80 leading-relaxed font-medium">
                {article.directAnswer}
              </p>
            </div>
          </Reveal>

          <div className="mt-14 space-y-14">
            {article.sections.map((s, i) => (
              <Reveal key={s.heading}>
                <div data-testid={`article-section-${i + 1}`}>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-charcoal">
                    {s.heading}
                  </h2>
                  {s.paragraphs.map((p, j) => (
                    <p key={j} className="mt-4 text-base text-charcoal/65 leading-relaxed">
                      {p}
                    </p>
                  ))}
                  {s.list && (
                    <ul className="mt-4 space-y-2">
                      {s.list.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-base text-charcoal/65">
                          <span className="mt-2.5 h-1.5 w-1.5 rotate-45 bg-gold shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14">
            <div className="bg-cream border border-charcoal/10 p-6 md:p-8" data-testid="article-service-links">
              <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-4">
                Related Services
              </div>
              <div className="flex flex-col gap-2">
                {article.serviceLinks.map((l) => (
                  <ArrowLink key={l.to} to={l.to} testid={`article-service-${l.to.replace(/\//g, "-")}`}>
                    {l.anchor}
                  </ArrowLink>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal className="mt-16">
            <SectionTag className="mb-6">Questions Agencies Ask</SectionTag>
            <Accordion type="single" collapsible className="w-full" data-testid="article-faq">
              {article.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-charcoal/10">
                  <AccordionTrigger className="text-left text-base md:text-lg font-semibold tracking-tight text-charcoal hover:text-tan hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-charcoal/65 leading-relaxed pb-6 max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal className="mt-16">
            <h2 className="text-2xl font-bold tracking-tight text-charcoal mb-6">
              Keep Reading
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/resources/${r.slug}`}
                  data-testid={`article-related-${r.slug}`}
                  className="group bg-cream border border-charcoal/10 p-5 transition-colors duration-300 hover:border-gold/50"
                >
                  <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-tan">
                    {r.cluster}
                  </span>
                  <span className="mt-2 block text-sm font-semibold tracking-tight text-charcoal leading-snug group-hover:text-tan transition-colors">
                    {r.title}
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title={article.cta.label}
        copy="Tell us about your agency and we will show you how this applies to your market and season."
        testid="article-cta-band"
      />
    </main>
  );
}
